/**
 * Group Management state.
 *
 * Mirrors monolith globals:
 *   cachedGroupResults, groupMgmtCreateState,
 *   window.currentGroupForMembers, window.groupMgmtSelectedUsers,
 *   window.groupSmtpGroups, window.groupSmtpState
 */

import { create } from 'zustand';
import {
  listDeviceFollow,
  createDeviceFollow,
  searchGroups,
  fetchAllGroups,
  fetchGroupMembers,
  fetchGroupEmailAddresses,
  fetchExchangeDomains,
  addUserToGroup,
  removeUserFromGroup,
  createGroupV2,
  createExchangeDl,
  fetchDynamicGroupLogs,
  fetchGroupMembershipChanges,
  updateAllDynamicGroups as apiUpdateAllDynamicGroups,
  fetchBusinessUnitsWithSites,
  fetchGroupNamePrefix,
  fetchDynamicExclusions,
  excludeDynamicMember,
  includeDynamicMember
} from '@/api/endpoints/groups.js';

/**
 * Which exclusion mechanism a dynamic group uses, or null if it has none.
 *  - 'azure': Entra evaluates a membershipRule — exclusions edit Omni's clause.
 *  - 'ad':    Omni evaluates its own stored rules — exclusions are a list.
 * Exported so the row and the modal cannot disagree.
 */
export function dynamicExclusionKind(group) {
  if (!group || group.isDynamic !== true || group.isRoleAssignable === true) return null;
  if (group.membershipRule && isEntraObjectId(group.azureId)) return 'azure';
  const dn = String(group.dn || '');
  if (!group.membershipRule && dn.includes('CN=')) return 'ad';
  return null;
}

/** Derive platform from group properties per CLAUDE.md rules. */
export function derivePlatform(group) {
  const explicitType = String(group.type || '').toLowerCase();
  if (explicitType) return explicitType;
  const dn = group.dn || '';
  const azureId = group.azureId || '';
  if (azureId && dn && dn !== azureId) return 'synced';
  if (azureId && (!dn || dn === azureId)) return 'azure';
  if (dn && !azureId) return 'ad';
  return 'azure';
}

/** Safe fallbacks per CLAUDE.md. */
export function safeGroupProps(group) {
  const safeDn   = group.dn || group.azureId || group.name || '';
  const safeName = group.name || group.displayName || 'Unknown';
  const safeType = derivePlatform(group);
  return { safeDn, safeName, safeType };
}

const ENTRA_OBJECT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True only for a real Microsoft Entra object id (GUID) — never a UPN/email/DN.
 *  The cache-first candidate search stores a UPN in `id`/`azureId`, so it must
 *  not be treated as an object id when adding members (Graph rejects it). */
export function isEntraObjectId(value) {
  return ENTRA_OBJECT_ID_RE.test(String(value ?? '').trim());
}

/** Stable identity key for a candidate/member object. Computed from data (not
 *  object identity) and used for BOTH the result rows and the selection list so
 *  a member that carries an on-prem DN *and* a UPN can't key differently in the
 *  two places (that mismatch was leaving ticked checkboxes visually unchecked). */
export function memberKey(m) {
  if (!m) return '';
  return m.dn || m.distinguishedName || m.azureId || m.id || m.userPrincipalName || m.mail || m.displayName || m.name || '';
}

/** The set of strong identities a member object can be recognised by. Excludes
 *  `id` on purpose — it holds a UPN for cache-sourced members but a GUID for
 *  Graph-sourced ones, so it can't be compared like-for-like. */
function memberIdentitySet(m) {
  const norm = (v) => String(v ?? '').trim().toLowerCase();
  return new Set([m?.azureId, m?.dn || m?.distinguishedName, m?.userPrincipalName, m?.mail].map(norm).filter(Boolean));
}

/** True if two member objects refer to the same person/group/device even when
 *  represented differently (cache row keyed by UPN vs Graph row keyed by GUID).
 *  Used to reconcile optimistic list edits with the authoritative directory read. */
export function sameMemberIdentity(a, b) {
  if (!a || !b) return false;
  const sa = memberIdentitySet(a);
  for (const id of memberIdentitySet(b)) if (sa.has(id)) return true;
  const ka = memberKey(a);
  return ka !== '' && ka === memberKey(b);
}

/** Map a selected candidate to the shape the member list renders, so an
 *  optimistically-added member displays correctly (name, UPN, type). */
function toMemberDisplayShape(m) {
  return {
    displayName: m.displayName || m.name || 'Unknown',
    name: m.name || m.displayName || 'Unknown',
    userPrincipalName: m.userPrincipalName || '',
    mail: m.mail || '',
    dn: m.dn || m.distinguishedName || null,
    azureId: isEntraObjectId(m.azureId) ? m.azureId : (isEntraObjectId(m.id) ? m.id : null),
    memberType: m.memberType || 'user',
    id: m.id || m.userPrincipalName || ''
  };
}

const sortByDisplayName = (a, b) =>
  String(a.displayName || a.name || '').localeCompare(String(b.displayName || b.name || ''), undefined, { sensitivity: 'base' });

/** Is this group a dynamic AD or synced group (eligible for Live Update)? */
export function isADDynamic(group) {
  const { safeDn, safeType } = safeGroupProps(group);
  return safeType === 'ad' || (safeType === 'synced' && safeDn.includes('CN='));
}

const CREATE_INITIAL = {
  step: 1,
  // lane: 'ad-security' | 'azure-security' | 'm365' | 'exchange-dl' | 'computer'
  lane: '',
  isDynamic: false,
  // Business & site
  businessUnit: '',
  businessUnitDn: '',
  site: '',                 // '' == All Sites
  // Naming (locked prefix + editable suffix)
  namePrefix: '',           // e.g. 'SG-CTR-MID-' / 'CG-CTR-' / 'M365-CTR-'
  namePrefixLoading: false,
  siteEmailDomain: '',      // default email domain from Sites config (site → business fallback)
  nameSuffix: '',
  description: '',
  // Mail (m365 / exchange-dl)
  emailLocal: '',
  emailDomain: '',
  emailAliases: [],
  ownerDn: '',
  ownerName: '',
  ownerUpn: '',
  addTeams: false,
  deliverToMembers: true,
  blockExternal: false,
  // Static members (camelCase member/device objects)
  membersToAdd: [],
  // AD dynamic rules (PascalCase, wrapped into a single RuleGroup on submit)
  ruleGroups: [],           // [{ Attribute, Operator, Values, Logic }]
  groupLogic: 'AND',
  // Azure/M365/CG dynamic
  membershipRule: '',
  nameAvailability: null,   // null | 'checking' | 'available' | 'taken'
  // Exchange DL (classic) fields
  senderAccess: 'Open',     // 'Open' | 'InternalOnly' | 'Specific'
  allowedSenders: '',       // comma/newline separated emails (Specific only)
  dlAliases: '',            // comma separated alias local/full addresses
  // Device group that follows users (lane 'device-follow')
  followSources: [],        // [{ id, name }] user groups whose people's devices belong
  followKinds: 'computers', // 'computers' | 'phones' | 'both' (phones -> MG- prefix)
  followGrace: 24           // hours a device waits before it is removed
};

/** Map the wizard lane + dynamic toggle to a backend create mode. */
function laneToMode(lane, isDynamic) {
  switch (lane) {
    case 'ad-security':    return isDynamic ? 'ad-dynamic' : 'ad-security';
    case 'azure-security': return isDynamic ? 'azure-dynamic' : 'azure-security';
    case 'm365':           return isDynamic ? 'm365-dynamic' : 'm365';
    case 'computer':       return isDynamic ? 'computer-dynamic' : 'computer-static';
    default:               return 'ad-security';
  }
}

/** Type param for the name-prefix endpoint. A device group that follows
 *  users is MG- when it holds phones only, otherwise CG-. */
function laneToPrefixType(lane, followKinds) {
  if (lane === 'device-follow') return followKinds === 'phones' ? 'mobile' : 'computer';
  if (lane === 'computer') return 'computer';
  if (lane === 'm365') return 'm365';
  if (lane === 'exchange-dl') return 'exchange-dl';
  return 'security'; // ad-security + azure-security -> SG
}

export const useGroupMgmtStore = create((set, get) => ({
  /* ---------- search results ---------- */
  cachedGroupResults: [],
  filteredResults: [],
  searchQuery: '',
  searching: false,
  searchError: null,
  hasSearched: false,

  /* ---------- filters ---------- */
  memberMgmtFilter: '',    // '' | 'dynamic' | 'static'
  groupTypeFilter: '',     // '' | 'Security Group' | 'Distribution Group' | 'M365 Group' | 'Mail-Enabled Security'
  platformFilter: '',      // '' | 'azure' | 'synced' | 'ad'

  /* ---------- member modal ---------- */
  membersOpen: false,
  membersGroup: null,      // { safeDn, safeName, safeType, azureId }
  membersLoading: false,
  membersData: [],
  membersError: null,
  membersViewOnly: false,
  // Which screen opened the members modal. Tabs stay mounted when hidden, so
  // Group Management and Security Hub → Web Filtering both render the modal;
  // each renders it only for its own opens, or it would appear twice.
  membersOwner: 'group-management',
  // Dynamic-group exclusions for the group the members modal is showing.
  membersDynamicKind: null, // null | 'ad' | 'azure'
  exclusions: [],
  exclusionsLoading: false,
  exclusionsError: null,
  exclusionsMeta: null,     // { ruleLength, ruleMaxLength } for Azure groups

  /* ---------- add members modal ---------- */
  addMembersOpen: false,
  addMembersMode: 'users', // 'users' | 'devices'
  addMembersResults: [],
  addMembersSearching: false,
  selectedMembers: [],

  /* ---------- SMTP modal ---------- */
  smtpOpen: false,
  smtpGroup: null,
  smtpLoading: false,
  smtpPrimary: '',
  smtpAliases: [],
  smtpDomains: [],
  smtpDomainsLoaded: false,
  smtpSaving: false,
  smtpError: null,

  /* ---------- logs modal ---------- */
  logsOpen: false,
  logsGroup: null,
  logsData: [],
  logsLoading: false,
  // Entra directory-audit membership changes (Azure-evaluated groups only).
  logsChanges: [],
  logsChangesLoading: false,
  logsChangesMeta: null,

  /* ---------- device groups that follow user groups ---------- */
  followOpen: false,
  /** Group to manage or convert when opening; null = the list. */
  followGroup: null,
  // Apps tab phase 3: the group whose Apps window is open.
  appsGroup: null,
  /** Lower-cased Entra ids of groups that follow users (for the row chip). */
  followIds: [],
  /** Lower-cased Entra id → summary { mode, sources, kinds, status, lastRunAt, desired, error } for the row. */
  followById: {},

  /* ---------- rule editors ----------
   * adRuleGroup    — tool-managed AD dynamic rules (config.json / updater)
   * azureRuleGroup — Azure AD membershipRule expression (Graph)
   * Two different systems; never confuse them. */
  adRuleGroup: null,
  azureRuleGroup: null,

  /* ---------- create wizard ---------- */
  createOpen: false,
  create: { ...CREATE_INITIAL },
  businessUnits: [],
  businessUnitsLoaded: false,
  creating: false,

  /* ============== computed helpers ============== */

  applyFilters(source) {
    const { memberMgmtFilter, groupTypeFilter, platformFilter } = get();
    let result = source;

    if (groupTypeFilter) {
      result = result.filter(g => g.groupType === groupTypeFilter);
    }
    // Groups that follow users are Omni-managed, so they count as dynamic here.
    const follow = get().followById;
    const isFollow = (g) => Boolean(g.azureId) && Boolean(follow[String(g.azureId).toLowerCase()]);
    if (memberMgmtFilter === 'dynamic') {
      result = result.filter(g => g.isDynamic === true || isFollow(g));
    } else if (memberMgmtFilter === 'static') {
      result = result.filter(g => g.isDynamic !== true && !isFollow(g));
    } else if (memberMgmtFilter === 'follow') {
      result = result.filter(isFollow);
    }
    if (platformFilter) {
      result = result.filter(g => {
        const platform = derivePlatform(g);
        return platform === platformFilter;
      });
    }
    return result;
  },

  /* ---------- business/site search scoping (server-side) ---------- */
  businessFilter: '',
  siteFilter: '',

  /* ============== search actions ============== */

  setSearchQuery(q) { set({ searchQuery: q }); },

  /** Select a business scope (clears site). Re-runs the search so results
   *  reflect the new scope — including empty-query browse. */
  setBusinessFilter(name) {
    set({ businessFilter: name, siteFilter: '' });
    const { hasSearched } = get();
    if (name || hasSearched) get().runSearch();
  },

  setSiteFilter(name) {
    set({ siteFilter: name });
    get().runSearch();
  },

  setFilter(key, value) {
    set({ [key]: value });
    const { cachedGroupResults, applyFilters } = get();
    if (cachedGroupResults.length > 0) set({ filteredResults: applyFilters(cachedGroupResults) });
  },

  // Monotonic sequence so a slow older search response can never clobber the
  // results of a newer one (search-as-you-type fires overlapping requests).
  _searchSeq: 0,

  async runSearch() {
    const { searchQuery, applyFilters, businessFilter, siteFilter } = get();
    const query = searchQuery.trim();
    const business = businessFilter || undefined;
    const site = business ? (siteFilter || undefined) : undefined;
    const seq = get()._searchSeq + 1;
    set({ _searchSeq: seq, searching: true, searchError: null, hasSearched: true });

    try {
      const data = query
        ? await searchGroups(query, business, site)
        : await fetchAllGroups(business, site);
      if (get()._searchSeq !== seq) return; // superseded by a newer search
      const groups = data?.groups ?? [];
      const filtered = applyFilters(groups);
      set({ cachedGroupResults: groups, filteredResults: filtered, searching: false });
    } catch (err) {
      if (get()._searchSeq !== seq) return;
      set({ searching: false, searchError: err.message || 'Failed to search groups' });
    }
  },

  /* ============== member modal actions ============== */

  async openMembers(group, viewOnly, owner = 'group-management') {
    const { safeDn, safeName, safeType } = safeGroupProps(group);
    const azureId = group.azureId || '';
    const dynamicKind = dynamicExclusionKind(group);
    set({
      membersOpen: true,
      membersGroup: { safeDn, safeName, safeType, azureId, groupType: group.groupType || '' },
      membersLoading: true,
      membersData: [],
      membersError: null,
      membersViewOnly: viewOnly,
      membersOwner: owner,
      membersDynamicKind: dynamicKind,
      exclusions: [],
      exclusionsError: null,
      exclusionsMeta: null
    });
    if (dynamicKind) void get().loadExclusions();
    try {
      const data = await fetchGroupMembers(safeDn, azureId || undefined);
      if (get().membersGroup?.safeDn !== safeDn) return;
      set({ membersData: data?.members ?? [], membersLoading: false });
    } catch (err) {
      if (get().membersGroup?.safeDn !== safeDn) return;
      set({ membersLoading: false, membersError: err.message });
    }
  },

  closeMembers() {
    set({ membersOpen: false, membersGroup: null, membersData: [], membersError: null, membersDynamicKind: null, exclusions: [], exclusionsError: null, exclusionsMeta: null });
  },

  /** The identity the API needs for this group's exclusions. */
  exclusionTarget() {
    const { membersGroup, membersDynamicKind } = get();
    if (!membersGroup || !membersDynamicKind) return null;
    return membersDynamicKind === 'azure'
      ? { azureId: membersGroup.azureId }
      : { groupName: membersGroup.safeName };
  },

  async loadExclusions() {
    const target = get().exclusionTarget();
    if (!target) return;
    const safeDn = get().membersGroup?.safeDn;
    set({ exclusionsLoading: true, exclusionsError: null });
    try {
      const data = await fetchDynamicExclusions(target);
      if (get().membersGroup?.safeDn !== safeDn) return;
      set({
        exclusions: Array.isArray(data?.exclusions) ? data.exclusions : [],
        exclusionsMeta: typeof data?.ruleMaxLength === 'number' ? { ruleLength: data.ruleLength ?? 0, ruleMaxLength: data.ruleMaxLength } : null,
        exclusionsLoading: false
      });
    } catch (err) {
      if (get().membersGroup?.safeDn !== safeDn) return;
      // An unreadable list must never look like "nobody is excluded".
      set({ exclusionsLoading: false, exclusionsError: err.message || 'Could not read the exclusion list.' });
    }
  },

  /** Exclude a member. Throws with the server's message on refusal. */
  async excludeMember(member, { ticket, reason }) {
    const target = get().exclusionTarget();
    if (!target) throw new Error('This group does not support exclusions.');
    const kind = get().membersDynamicKind;
    const result = await excludeDynamicMember({
      ...target,
      ticket,
      reason,
      member: {
        id: member.id || '',
        azureId: member.azureId || '',
        dn: member.dn || '',
        userPrincipalName: member.userPrincipalName || '',
        samAccountName: member.samAccountName || '',
        displayName: member.displayName || member.name || '',
        memberType: member.memberType || 'user'
      }
    });
    // AD: Omni removed them from the group just now. Azure: they stay listed
    // until Entra processes the rule, and the row says so instead.
    if (kind === 'ad') {
      set({ membersData: get().membersData.filter((m) => !sameMemberIdentity(m, member)) });
    }
    void get().loadExclusions();
    return result;
  },

  async includeExclusion(exclusion, { ticket, reason } = {}) {
    const target = get().exclusionTarget();
    if (!target) throw new Error('This group does not support exclusions.');
    const exclusionId = exclusion.id || exclusion.userPrincipalName || exclusion.dn || exclusion.samAccountName || '';
    const result = await includeDynamicMember({ ...target, exclusionId, ticket, reason });
    set({ exclusions: get().exclusions.filter((e) => e !== exclusion) });
    void get().loadExclusions();
    return result;
  },

  async removeMember(member) {
    const { membersGroup } = get();
    if (!membersGroup) return;
    const { safeDn, safeType, azureId } = membersGroup;

    const memberIdentifier = member.dn || member.userPrincipalName || member.azureId || '';
    const mType = member.memberType || 'user';
    const mAzureId = member.azureId || '';

    const payload = { groupDN: safeDn, groupType: safeType, azureId, memberType: mType };
    if (mType === 'device') {
      payload.memberAzureId = mAzureId || memberIdentifier;
      payload.userDN = memberIdentifier;
    } else {
      payload.userDN = memberIdentifier;
      payload.memberAzureId = mAzureId;
      payload.userUpn = member.userPrincipalName || '';
    }

    await removeUserFromGroup(payload);
    // Update the on-screen list immediately — a directory read straight after the
    // write can still show the old membership (Exchange DL changes especially lag
    // a few seconds) — then reconcile against the source of truth in the
    // background without reverting the change the user just made.
    set({ membersData: get().membersData.filter((m) => !sameMemberIdentity(m, member)) });
    void get().reconcileMembers([], [member]);
  },

  /**
   * Background reconcile: after a short delay (directory read-after-write lag),
   * refetch the authoritative member list and apply the user's just-made delta
   * on top of it — so a still-lagging read can never revert an add/remove. Bails
   * if the members modal has moved to a different group meanwhile.
   */
  async reconcileMembers(addedMembers = [], removedMembers = []) {
    const start = get().membersGroup;
    if (!start) return;
    const { safeDn, azureId } = start;
    await new Promise((resolve) => setTimeout(resolve, 3500));
    if (get().membersGroup?.safeDn !== safeDn) return; // modal moved on
    let data;
    try {
      data = await fetchGroupMembers(safeDn, azureId || undefined);
    } catch {
      return; // keep the optimistic state on a transient read failure
    }
    if (get().membersGroup?.safeDn !== safeDn) return;
    let list = Array.isArray(data?.members) ? data.members : [];
    if (removedMembers.length) list = list.filter((m) => !removedMembers.some((rm) => sameMemberIdentity(m, rm)));
    for (const am of addedMembers) {
      if (!list.some((m) => sameMemberIdentity(m, am))) list = [...list, am];
    }
    set({ membersData: [...list].sort(sortByDisplayName) });
  },

  /* ============== add members modal actions ============== */

  openAddMembers() { set({ addMembersOpen: true, addMembersMode: 'users', addMembersResults: [], selectedMembers: [] }); },
  closeAddMembers() { set({ addMembersOpen: false, addMembersResults: [], selectedMembers: [] }); },

  setAddMembersMode(mode) {
    set({ addMembersMode: mode, addMembersResults: [], selectedMembers: [] });
  },

  setAddMembersResults(results) { set({ addMembersResults: results }); },
  setAddMembersSearching(v) { set({ addMembersSearching: v }); },

  toggleSelectedMember(item) {
    const { selectedMembers } = get();
    const key = memberKey(item);
    if (selectedMembers.some(m => memberKey(m) === key)) {
      set({ selectedMembers: selectedMembers.filter(m => memberKey(m) !== key) });
    } else {
      set({ selectedMembers: [...selectedMembers, item] });
    }
  },

  // Dedupe by stable key so re-selecting can never create duplicate rows.
  selectAllMembers(items) {
    set({ selectedMembers: [...new Map(items.map(m => [memberKey(m), m])).values()] });
  },
  clearSelectedMembers() { set({ selectedMembers: [] }); },

  async bulkAddMembers() {
    const { selectedMembers, membersGroup } = get();
    if (!membersGroup) return { success: 0, fail: 0 };
    const { safeDn, safeType, azureId } = membersGroup;

    let success = 0;
    let fail = 0;
    let lastError = null;
    const added = [];
    for (const member of selectedMembers) {
      try {
        const mType = member.memberType || 'user';
        // Only pass an id the backend can safely use as an Entra object id — a
        // GUID, never the UPN the cache-first search puts in id/azureId. Always
        // send the UPN/mail too so the Azure path can resolve the real GUID.
        const objectId = isEntraObjectId(member.azureId)
          ? member.azureId
          : (isEntraObjectId(member.id) ? member.id : '');
        const payload = {
          groupDN: safeDn,
          groupType: safeType,
          azureId,
          memberType: mType,
          userDN: member.dn || member.distinguishedName || '',
          memberAzureId: objectId,
          userUpn: member.userPrincipalName || member.mail || ''
        };
        await addUserToGroup(payload);
        success++;
        added.push(toMemberDisplayShape(member));
      } catch (err) {
        fail++;
        // Surface the server message (e.g. 409 view-only for dynamic/CG groups).
        lastError = err?.message || lastError;
      }
    }

    // Show the successful adds on the open member list immediately (directory
    // reads lag a few seconds after the write, esp. Exchange distribution lists),
    // then reconcile against the source of truth in the background.
    if (added.length) {
      const merged = [...get().membersData];
      for (const m of added) {
        if (!merged.some((x) => sameMemberIdentity(x, m))) merged.push(m);
      }
      set({ membersData: merged.sort(sortByDisplayName) });
      void get().reconcileMembers(added, []);
    }
    set({ selectedMembers: [] });

    return { success, fail, lastError };
  },

  /* ============== SMTP modal actions ============== */

  async openSmtp(group) {
    const { safeDn, safeName, safeType } = safeGroupProps(group);
    const smtpGroup = {
      dn: safeDn,
      name: safeName,
      type: safeType,
      azureId: group.azureId || '',
      mail: group.mail || '',
      groupType: group.groupType || ''
    };
    set({
      smtpOpen: true,
      smtpGroup,
      smtpLoading: true,
      smtpPrimary: '',
      smtpAliases: [],
      smtpError: null
    });

    try {
      const [addressData] = await Promise.all([
        fetchGroupEmailAddresses(smtpGroup),
        get().loadSmtpDomains()
      ]);
      set({
        smtpPrimary: addressData?.primary ?? '',
        smtpAliases: Array.isArray(addressData?.aliases) ? addressData.aliases : [],
        smtpLoading: false
      });
    } catch (err) {
      set({ smtpLoading: false, smtpError: err.message });
    }
  },

  async loadSmtpDomains() {
    const { smtpDomainsLoaded, smtpDomains } = get();
    if (smtpDomainsLoaded && smtpDomains.length > 0) return smtpDomains;
    const { fetchExchangeDomains: fetchFn } = await import('@/api/endpoints/groups.js');
    const data = await fetchFn();
    const domains = (data?.domains ?? []).map(d => d.name || d).filter(Boolean);
    set({ smtpDomains: domains, smtpDomainsLoaded: true });
    return domains;
  },

  closeSmtp() {
    set({ smtpOpen: false, smtpGroup: null, smtpPrimary: '', smtpAliases: [], smtpError: null });
  },

  addSmtpAlias(alias) {
    const { smtpAliases } = get();
    set({ smtpAliases: [...smtpAliases, alias] });
  },

  removeSmtpAlias(index) {
    const { smtpAliases } = get();
    set({ smtpAliases: smtpAliases.filter((_, i) => i !== index) });
  },

  promoteSmtpAlias(index) {
    const { smtpPrimary, smtpAliases } = get();
    const newPrimary = smtpAliases[index];
    if (!newPrimary) return;
    const remaining = smtpAliases.filter((_, i) => i !== index);
    if (smtpPrimary && !remaining.some(a => a.toLowerCase() === smtpPrimary.toLowerCase())) {
      remaining.unshift(smtpPrimary.toLowerCase());
    }
    set({ smtpPrimary: newPrimary.toLowerCase(), smtpAliases: remaining });
  },

  /* ============== logs modal actions ============== */

  async openLogs(group) {
    const { safeDn, safeName } = safeGroupProps(group);
    // Only a real Entra object id can be audited. A cache-sourced `azureId`
    // can be a UPN (see isEntraObjectId), and asking Graph for audits against
    // one would 400 rather than return nothing useful.
    const azureId = isEntraObjectId(group?.azureId) ? group.azureId : null;
    set({
      logsOpen: true,
      logsGroup: { safeDn, safeName, azureId },
      logsLoading: true,
      logsData: [],
      logsChanges: [],
      logsChangesMeta: null,
      logsChangesLoading: Boolean(azureId)
    });

    // Two independent sources: the tool's own sync log (AD groups it manages)
    // and Entra's audit log (Azure-evaluated groups). Settled separately so a
    // failure in one never blanks the other.
    const jobs = [
      fetchDynamicGroupLogs(safeName)
        .then((data) => set({ logsData: data?.lines ?? [] }))
        .catch(() => {})
        .finally(() => set({ logsLoading: false }))
    ];
    if (azureId) {
      jobs.push(
        fetchGroupMembershipChanges(azureId)
          .then((data) => set({
            logsChanges: Array.isArray(data?.changes) ? data.changes : [],
            logsChangesMeta: {
              days: data?.days ?? null,
              complete: data?.complete !== false,
              unavailable: data?.unavailable ?? null
            }
          }))
          .catch((err) => set({ logsChangesMeta: { days: null, complete: false, unavailable: err?.message || 'Could not read the Entra audit log.' } }))
          .finally(() => set({ logsChangesLoading: false }))
      );
    }
    await Promise.allSettled(jobs);
  },

  closeLogs() {
    set({ logsOpen: false, logsGroup: null, logsData: [], logsChanges: [], logsChangesMeta: null });
  },

  openGroupApps(group) { set({ appsGroup: group }); },
  closeGroupApps() { set({ appsGroup: null }); },
  openDeviceFollow(group = null) { set({ followOpen: true, followGroup: group }); },
  closeDeviceFollow() { set({ followOpen: false, followGroup: null }); get().loadFollowIds(); },
  async loadFollowIds() {
    try {
      const data = await listDeviceFollow();
      const groups = data?.groups ?? [];
      const byId = {};
      for (const g of groups) {
        const run = g.lastRun;
        byId[String(g.id).toLowerCase()] = {
          mode: g.mode,
          kinds: g.kinds,
          graceHours: g.graceHours,
          sources: (g.sources ?? []).map((src) => src.name),
          status: g.running ? 'running' : (run?.status ?? null),
          lastRunAt: run?.finishedAt || run?.at || null,
          desired: run ? run.desiredCount : null,
          error: run?.error || null
        };
      }
      set({ followIds: Object.keys(byId), followById: byId });
      // The "Follows users" filter depends on this list, so re-apply it.
      const { memberMgmtFilter, cachedGroupResults, applyFilters } = get();
      if (memberMgmtFilter === 'follow' && cachedGroupResults.length) set({ filteredResults: applyFilters(cachedGroupResults) });
    } catch { /* row summary is cosmetic */ }
  },

  /* ============== rule editor actions ============== */

  openAdRuleBuilder(group) {
    const { safeDn, safeName } = safeGroupProps(group);
    set({ adRuleGroup: { name: safeName, dn: safeDn } });
  },
  closeAdRuleBuilder() { set({ adRuleGroup: null }); },

  openAzureRules(group) {
    const { safeName } = safeGroupProps(group);
    set({ azureRuleGroup: { name: safeName, azureId: group.azureId, membershipRule: group.membershipRule || '' } });
  },
  closeAzureRules() { set({ azureRuleGroup: null }); },

  /* ============== create wizard actions ============== */

  openCreate() {
    set({ createOpen: true, create: { ...CREATE_INITIAL } });
    get().loadBusinessUnits();
  },

  closeCreate() { set({ createOpen: false, create: { ...CREATE_INITIAL } }); },

  setCreateField(key, value) {
    set(state => ({ create: { ...state.create, [key]: value } }));
  },

  /** Switch the wizard lane — resets lane-specific state (dynamic toggle,
   *  rules, members) so nothing bleeds between lanes, clears the now-stale
   *  prefix, and recomputes it for the new lane's type code. */
  setCreateLane(lane) {
    set(state => ({
      create: {
        ...state.create,
        lane,
        isDynamic: false,          // exchange-dl is static-only; other lanes opt back in
        ruleGroups: [],
        groupLogic: 'AND',
        membershipRule: '',
        membersToAdd: [],
        followSources: [],
        followKinds: 'computers',
        followGrace: 24,
        nameAvailability: null,
        namePrefix: ''             // stale typeCode (SG- vs CG-/DG-) must not stick
      }
    }));
    if (get().create.businessUnit) {
      get().loadNamePrefixFor();
    }
  },

  async loadBusinessUnits() {
    if (get().businessUnitsLoaded) return;
    try {
      // fetchBusinessUnitsWithSites -> OuBusiness[] { name, dn, sites:[{name,dn}] }
      // (lowercase keys — fixes the G6 casing bug the old wizard hit reading u.Name).
      const data = await fetchBusinessUnitsWithSites();
      const list = Array.isArray(data) ? data : [];
      set({ businessUnits: list, businessUnitsLoaded: true });
    } catch {
      set({ businessUnitsLoaded: true });
    }
  },

  /** Resolve the locked name prefix (SG-/DG-/CG- + bizCode [+ siteCode]) for the
   *  current lane/business/site. Mirrors Printers/state.js loadNamePrefixFor. */
  async loadNamePrefixFor() {
    const { create } = get();
    const business = create.businessUnit;
    if (!business || !create.lane) {
      set((s) => ({ create: { ...s.create, namePrefix: '', namePrefixLoading: false } }));
      return;
    }
    set((s) => ({ create: { ...s.create, namePrefixLoading: true } }));
    try {
      const type = laneToPrefixType(create.lane, create.followKinds);
      const data = await fetchGroupNamePrefix(business, create.site || '', type);
      set((s) => ({ create: { ...s.create, namePrefix: data?.prefix || '', siteEmailDomain: data?.emailDomain || '', namePrefixLoading: false } }));
    } catch {
      set((s) => ({ create: { ...s.create, namePrefix: '', siteEmailDomain: '', namePrefixLoading: false } }));
    }
  },

  async submitCreate() {
    const { create } = get();
    set({ creating: true });
    const splitList = (value) => String(value || '').split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
    const fullName = `${create.namePrefix || ''}${create.nameSuffix || ''}`.trim();

    try {
      if (create.lane === 'exchange-dl') {
        // Classic Exchange distribution list — keep the dedicated cert-auth lane.
        const members = (create.membersToAdd || [])
          .map((m) => m.userPrincipalName || m.mail || m.email)
          .filter(Boolean);
        const result = await createExchangeDl({
          groupName: fullName,
          primaryEmail: `${create.emailLocal}@${create.emailDomain}`.toLowerCase(),
          description: create.description,
          groupMembershipType: 'Static',
          aliases: splitList(create.dlAliases),
          senderAccess: create.senderAccess || 'Open',
          allowedSenders: create.senderAccess === 'Specific' ? splitList(create.allowedSenders) : [],
          members
        });
        set({ creating: false });
        return { success: true, warnings: result?.warnings || [] };
      }

      if (create.lane === 'device-follow') {
        // An ordinary cloud security group that Omni keeps in step with the
        // devices of the chosen user groups' people. It starts in preview.
        const result = await createDeviceFollow({
          newGroupName: fullName,
          description: create.description,
          sources: create.followSources,
          kinds: create.followKinds,
          graceHours: Number(create.followGrace)
        });
        set({ creating: false });
        get().loadFollowIds();
        return { success: true, warnings: [], followGroup: result?.group ?? null };
      }

      const mode = laneToMode(create.lane, create.isDynamic);
      const body = {
        mode,
        groupName: fullName,
        description: create.description
      };

      if (create.lane === 'ad-security') {
        body.businessName = create.businessUnit;
        body.businessDn = create.businessUnitDn;
        if (create.isDynamic) {
          // AD dynamic rules: PascalCase RuleGroups AS AN ARRAY (fixes G5).
          body.rules = [{ GroupNumber: 1, Logic: create.groupLogic || 'AND', Rules: create.ruleGroups || [] }];
        } else {
          // Static members inline in the create body (fixes G7 — no post-create add loop).
          body.members = (create.membersToAdd || []).map((m) => ({
            dn: m.dn || null,
            userPrincipalName: m.userPrincipalName || '',
            mail: m.mail || '',
            displayName: m.displayName || m.name || '',
            name: m.name || m.displayName || '',
            memberType: m.memberType || 'user',
            source: 'ad'
          }));
        }
      } else if (create.lane === 'azure-security' || create.lane === 'm365') {
        if (create.lane === 'm365') {
          body.mailNickname = fullName;
          if (create.emailLocal && create.emailDomain) {
            body.primaryEmail = `${create.emailLocal}@${create.emailDomain}`.toLowerCase();
          }
          body.emailAliases = create.emailAliases || [];
          body.ownerId = create.ownerUpn || undefined;
          body.addTeams = !!create.addTeams;
          body.blockExternal = !!create.blockExternal;
          body.deliverToMembers = create.deliverToMembers !== false;
        }
        if (create.isDynamic) {
          body.membershipRule = create.membershipRule || '';
        } else {
          body.members = (create.membersToAdd || []).map((m) => ({
            azureId: m.azureId || m.id || null,
            id: m.id || m.azureId || null,
            userPrincipalName: m.userPrincipalName || '',
            mail: m.mail || '',
            displayName: m.displayName || m.name || '',
            name: m.name || m.displayName || '',
            memberType: m.memberType || 'user',
            source: 'azure'
          }));
        }
      } else if (create.lane === 'computer') {
        if (create.isDynamic) {
          body.membershipRule = create.membershipRule || '';
        } else {
          // CG static: device directory object ids as memberIds.
          body.memberIds = (create.membersToAdd || [])
            .map((m) => m.azureId || m.id)
            .filter(Boolean);
        }
      }

      const result = await createGroupV2(body);
      set({ creating: false });
      return { success: true, warnings: result?.warnings || [], result };
    } catch (err) {
      set({ creating: false });
      return { success: false, error: err.message };
    }
  },

  /* ============== "Update All Dynamic Groups" action ============== */
  async updateAllDynamic() {
    await apiUpdateAllDynamicGroups();
  }
}));
